// Theme scripts. Vanilla JS, no jQuery.

// Header background once the page scrolls
const siteHeader = document.querySelector(".header");
if (siteHeader) {
  let headerTicking = false;
  const updateHeader = () => {
    headerTicking = false;
    siteHeader.classList.toggle("is-scrolled", window.scrollY > 160);
  };
  window.addEventListener("scroll", () => {
    if (!headerTicking) {
      headerTicking = true;
      window.requestAnimationFrame(updateHeader);
    }
  }, {
    passive: true
  });
  updateHeader();
}

// Collapsed header menu (below $break-nav): full-screen dialog with focus trapped inside
const headerToggle = document.querySelector(".header__toggle");
if (headerToggle) {
  const header = headerToggle.closest(".header");
  const panel = document.getElementById(headerToggle.getAttribute("aria-controls"));
  const closeButton = panel.querySelector(".header__close");
  const collapsed = window.matchMedia("(max-width: 1279px)");
  const isOpen = () => header.classList.contains("is-open");
  let closingTimer;
  const setOpen = (open, returnFocus = true) => {
    headerToggle.setAttribute("aria-expanded", String(open));
    header.classList.toggle("is-open", open);

    // Keeps the header solid (no blur) while the panel fades out; see _header.scss
    clearTimeout(closingTimer);
    header.classList.toggle("is-closing", !open);
    if (!open) closingTimer = setTimeout(() => header.classList.remove("is-closing"), 300);
    document.documentElement.classList.toggle("has-menu-open", open);
    if (open) {
      panel.setAttribute("role", "dialog");
      panel.setAttribute("aria-modal", "true");
      panel.setAttribute("aria-label", "Menu");
      closeButton.focus();
    } else {
      panel.removeAttribute("role");
      panel.removeAttribute("aria-modal");
      panel.removeAttribute("aria-label");
      if (returnFocus) headerToggle.focus();
    }
  };
  headerToggle.addEventListener("click", () => setOpen(true));
  closeButton.addEventListener("click", () => setOpen(false));

  // Following a link (e.g. an in-page anchor) closes the menu
  panel.querySelectorAll("a[href]").forEach(link => {
    link.addEventListener("click", () => {
      if (isOpen()) setOpen(false, false);
    });
  });
  document.addEventListener("keydown", event => {
    if (!isOpen()) return;
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [...panel.querySelectorAll("a[href], button:not([disabled])")].filter(element => element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  // Growing past the collapsed layout closes the dialog so the page doesn't stay locked
  collapsed.addEventListener("change", event => {
    if (!event.matches && isOpen()) setOpen(false, false);
  });
}

// Hero video: desktop only and never with reduced motion. The poster stays as the LCP
// image; the video source is attached afterwards and fades in once it plays.
document.querySelectorAll(".hero__video[data-src]").forEach(video => {
  const wide = window.matchMedia("(min-width: 901px)");
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
  const load = () => {
    if (!wide.matches || reduced.matches || video.src) return;
    video.src = video.dataset.src;
    video.addEventListener("playing", () => video.classList.add("is-playing"), {
      once: true
    });
    video.play().catch(() => {});
  };
  const update = () => {
    if (reduced.matches) {
      video.pause();
      return;
    }
    load();
    if (video.src && wide.matches) video.play().catch(() => {});
  };
  load();
  wide.addEventListener("change", update);
  reduced.addEventListener("change", update);
});

// Hero parallax: the background scrolls slower than the page. Skipped with reduced
// motion; the section clips the media, so no gap shows while it moves.
document.querySelectorAll(".hero__media").forEach(media => {
  const hero = media.closest(".hero");
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
  const speed = 0.3;
  let ticking = false;
  const update = () => {
    ticking = false;
    if (reduced.matches) {
      media.style.transform = "";
      return;
    }
    const rect = hero.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) return;
    media.style.transform = `translate3d(0, ${Math.round(-rect.top * speed)}px, 0)`;
  };
  const request = () => {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(update);
    }
  };
  window.addEventListener("scroll", request, {
    passive: true
  });
  window.addEventListener("resize", request);
  reduced.addEventListener("change", request);
  update();
});

// Credential tabs: ARIA tabs from 768px up, an accordion below. Without JS every section is open.
document.querySelectorAll(".credential-tabs").forEach(root => {
  const list = root.querySelector(".credential-tabs__list");
  const tabs = [...root.querySelectorAll(".credential-tabs__tab")];
  const toggles = [...root.querySelectorAll(".credential-tabs__toggle")];
  const sections = [...root.querySelectorAll(".credential-tabs__section")];
  const contents = [...root.querySelectorAll(".credential-tabs__content")];
  const desktop = window.matchMedia("(min-width: 768px)");
  const select = (index, focus = false) => {
    tabs.forEach((tab, i) => {
      tab.setAttribute("aria-selected", String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
    });
    sections.forEach((section, i) => section.classList.toggle("is-active", i === index));
    if (focus) tabs[index].focus();
  };
  const setOpen = (index, open) => {
    toggles[index].setAttribute("aria-expanded", String(open));
    sections[index].classList.toggle("is-open", open);
  };

  // Swap the ARIA pattern to match the layout on screen
  const applyMode = () => {
    const tabsMode = desktop.matches;
    if (tabsMode) {
      list.setAttribute("role", "tablist");
    } else {
      list.removeAttribute("role");
    }
    tabs.forEach(tab => tab.setAttribute("role", "tab"));
    contents.forEach((content, i) => {
      if (tabsMode) {
        content.setAttribute("role", "tabpanel");
        content.setAttribute("aria-labelledby", tabs[i].id);
        content.tabIndex = 0;
      } else {
        content.setAttribute("role", "region");
        content.setAttribute("aria-labelledby", toggles[i].id);
        content.removeAttribute("tabindex");
      }
    });
  };
  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => select(index));
    tab.addEventListener("keydown", event => {
      const last = tabs.length - 1;
      const next = {
        ArrowDown: index + 1,
        ArrowRight: index + 1,
        ArrowUp: index - 1,
        ArrowLeft: index - 1,
        Home: 0,
        End: last
      }[event.key];
      if (next === undefined) return;
      event.preventDefault();
      select((next + tabs.length) % tabs.length, true);
    });
  });
  toggles.forEach((toggle, index) => {
    toggle.addEventListener("click", () => setOpen(index, toggle.getAttribute("aria-expanded") !== "true"));
  });
  select(0);
  toggles.forEach((_, index) => setOpen(index, index === 0));
  applyMode();
  desktop.addEventListener("change", applyMode);
  root.classList.add("is-enhanced");
});

// Contact form (Contact Form 7): error summary with links to each field, and a confirmation
// panel that replaces the form once it is sent (Figma 229:888).
document.querySelectorAll(".contact-form").forEach(section => {
  const wrap = section.querySelector(".wpcf7");
  if (!wrap) return;
  const summary = section.querySelector(".contact-form__summary");
  const success = section.querySelector(".contact-form__success");
  const formBox = section.querySelector(".contact-form__form");
  const introBlock = section.querySelector(".contact-form__intro-block");
  const counts = ["", "One field needs", "Two fields need", "Three fields need", "Four fields need"];

  // Tie each helper text to its field for screen readers
  section.querySelectorAll(".contact-field").forEach(field => {
    const helper = field.querySelector(".contact-field__helper");
    const control = field.querySelector("input, textarea");
    if (helper && control) control.setAttribute("aria-describedby", helper.id);
  });
  const hideSummary = () => {
    summary.hidden = true;
    summary.replaceChildren();
  };
  wrap.addEventListener("wpcf7invalid", event => {
    const invalid = event.detail.apiResponse.invalid_fields || [];
    if (!invalid.length) return;
    const heading = document.createElement("p");
    heading.textContent = `${counts[invalid.length] || `${invalid.length} fields need`} attention.`;
    const list = document.createElement("ul");
    invalid.forEach(item => {
      const control = item.idref ? document.getElementById(item.idref) : null;
      const label = control ? section.querySelector(`label[for="${item.idref}"]`) : null;
      const li = document.createElement("li");
      const link = document.createElement("a");
      link.href = `#${item.idref}`;
      link.textContent = `${label ? label.textContent.trim() : "Field"} — ${item.message}`;
      link.addEventListener("click", clickEvent => {
        if (!control) return;
        clickEvent.preventDefault();
        control.focus();
      });
      li.append(link);
      list.append(li);
    });
    summary.replaceChildren(heading, list);
    summary.hidden = false;
    summary.focus();
  });
  wrap.addEventListener("wpcf7mailsent", () => {
    hideSummary();
    if (!success) return;
    formBox.hidden = true;
    if (introBlock) introBlock.hidden = true;
    success.hidden = false;
    success.focus();
  });
  ["wpcf7mailfailed", "wpcf7spam"].forEach(name => wrap.addEventListener(name, hideSummary));
});

// Legal index: always open on desktop, collapsed on mobile; marks the section being read
document.querySelectorAll(".legal-text").forEach(root => {
  const details = root.querySelector(".legal-text__toc-details");
  const links = [...root.querySelectorAll(".legal-text__toc-list a")];
  const desktop = window.matchMedia("(min-width: 901px)");
  const syncDetails = () => {
    details.open = desktop.matches;
  };
  syncDetails();
  desktop.addEventListener("change", syncDetails);

  // The summary is only a toggle on mobile
  details.querySelector("summary").addEventListener("click", event => {
    if (desktop.matches) event.preventDefault();
  });
  links.forEach(link => {
    link.addEventListener("click", () => {
      if (!desktop.matches) details.open = false;
    });
  });
  if (!("IntersectionObserver" in window)) return;
  const setCurrent = id => {
    links.forEach(link => link.setAttribute("aria-current", String(link.hash === `#${id}`)));
  };
  const observer = new IntersectionObserver(entries => {
    const visible = entries.filter(entry => entry.isIntersecting);
    if (visible.length) setCurrent(visible[0].target.id);
  }, {
    rootMargin: "-20% 0px -70% 0px"
  });
  root.querySelectorAll(".legal-text__section").forEach(section => observer.observe(section));
  if (links[0]) setCurrent(links[0].hash.slice(1));
});